<template>
  <WdsStack
    direction-column
    justify-space-between
    class="tw-h-full edit-group-modal"
    data-test="user-group-editor"
  >
    <WdsStack direction-column gap-md class="tw-mb-5">
      <WdsInput
        name="group_name"
        :placeholder="$t('Name')"
        :error="nameError"
        :model-value="name"
        data-test="user-group-name-input"
        @update:model-value="handleInput"
      >
        <template #label>
          <WdsText>
            {{ $t('Group name') }}
          </WdsText>
        </template>
      </WdsInput>
      <WdsSelect
        v-model="users"
        :label="$t('Add members')"
        multi-select
        :list="mapper"
        :load-more-distance="10"
        :error="usersError"
        name="user-group-members-select"
        @load-more="getMore"
      >
        <template #header="{ options }">
          <WdsStack align-center gap-sm @click="inputFocus = true">
            <WdsSpinner v-if="loading" class="inline" indeterminate :size="16" />
            <WdsIcon v-else name="search" class="inline" :size="16" />
            <span v-if="options?.length && !inputFocus">
              {{ $tc(':count selected', options.length) }}
            </span>
            <input
              ref="input"
              v-model="variables.search"
              type="text"
              class="tw-border-0"
              :placeholder="$t('Search by name')"
              @click="handleClickOnSelectInput"
              @keyup.prevent
            />
          </WdsStack>
        </template>

        <template #option="{ option }">

          <WdsAvatarLabel
            :text="option.name"
            :initials="option.initials"
            :path="option.avatarPath ?? ''"
            sm
            truncated
            class="tw-w-full"
          >
            <template #subText>
              {{ option.email }}
            </template>
          </WdsAvatarLabel>
        </template>
      </WdsSelect>

      <div v-if="users.length">
        <WdsText v-if="loadingAllUsers" secondary class="tw-mb-2">
          {{ $t('Loading all selected users ') }}
          <WdsSpinner class="inline" indeterminate :size="16" />
        </WdsText>
        <WdsText v-else secondary class="tw-mb-2">
          {{ $t('Selected') }} ({{ users.length }})
        </WdsText>
        <div class="tw-max-h-[300px] tw-overflow-y-auto tw-overflow-x-hidden selected-list">
          <div v-for="user of users" :key="user.id" class="selected-list-item">
            <WdsStack justify-space-between align-center>
              <WdsAvatarLabel
                :text="user.name"
                :initials="user.initials"
                :path="user.avatarPath"
                sm
                truncated
                class="tw-w-[90%]"
              >
                <template #subText>
                  {{ user.email }}
                </template>
              </WdsAvatarLabel>
              <WdsButtonIcon transparent-background sm @click="removeUser(user)">
                <WdsIcon name="close" />
              </WdsButtonIcon>
            </WdsStack>
          </div>
        </div>
      </div>

      <WdsSelect
        v-if="editMode"
        v-model="status"
        :placeholder="$t('Select user group status')"
        :label="$t('Status')"
        :list="statuses"
        :error="statusError"
      />

      <WdsInputArea
        v-model="description"
        name="group_description"
        :placeholder="$t('Add instructions for the group members...')"
        :error="descriptionError"
        :maxlength="maxNumberOfCharacters"
        data-test="user-group-description-input"
      >
        <template #label>
          <WdsStack gap-sm justify-space-between>
            <WdsStack gap-sm>
              <WdsText>
                {{ $t('Group instructions') }}
              </WdsText>
              <div>
                <WdsTag sm>
                  {{ $t('Optional') }}
                </WdsTag>
              </div>
            </WdsStack>
            <WdsTooltip>
              <template #tooltip-content>
                {{ $t('Maximum number of characters left for the instructions') }}
              </template>
              <WdsTag sm>{{ charactersLeft }} / {{ maxNumberOfCharacters }}</WdsTag>
            </WdsTooltip>
          </WdsStack>
        </template>
      </WdsInputArea>
      <WdsText secondary>
        {{
          $t('These instructions will be included in the approval request emails sent to approvers')
        }}
      </WdsText>
    </WdsStack>
    <WdsStack gap-sm justify-space-between align-center>
      <WdsButton @click="emit('cancel')">{{ $t('Cancel') }}</WdsButton>
      <WdsStack gap-sm align-center>
        <WdsSpinner v-if="submitLoading" indeterminate />

        <WdsButton v-if="editMode" error :disabled="submitLoading" @click="showDeleteModal = true">
          {{ $t('Delete group') }}
        </WdsButton>

        <WdsTooltip v-if="quietErrors.length > 0">
          <WdsButton primary disabled data-test="user-group-save-button">
            {{ $t('Save') }}
          </WdsButton>
          <template #tooltip-content>
            <span v-for="e in quietErrors" :key="e">
              {{ e }}
              <br />
            </span>
          </template>
        </WdsTooltip>
        <WdsButton
          v-else
          primary
          data-test="user-group-save-button"
          :disabled="submitLoading"
          @click="submitForm"
        >
          {{ $t('Save') }}
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsStack>
  <GroupDeleteModal
    :show="showDeleteModal"
    :group-name="name"
    @cancel="showDeleteModal = false"
    @submit="deleteGroup"
  />
</template>

<script lang="ts" setup>
import {
  WdsStack,
  WdsInput,
  WdsIcon,
  WdsText,
  WdsInputArea,
  WdsTooltip,
  WdsButton,
  WdsSelect,
  WdsSpinner,
  WdsTag,
  WdsAvatarLabel,
  WdsButtonIcon,
} from '@worksome/wds-components'
import gql from 'graphql-tag'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, reactive, ref, unref } from 'vue'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import useApolloError from '@/composables/useApolloError'
import useCustomVuelidate from '@/composables/useCustomVuelidate'
import useApolloAndVuelidate from '@/composables/useApolloAndVuelidate'
import {
  getAllUsersQuery,
  teamMembersAndOwnersQuery,
} from '@/components/Workflows/Pages/Groups/graphql/queries'
import {
  createUserGroupMutation,
  deleteUserGroupMutation,
  editUserGroupMutation,
} from '@/components/Workflows/Pages/Groups/graphql/mutations'
import GroupDeleteModal from '@/components/Workflows/Pages/Groups/GroupDeleteModal.vue'
import { UserGroupStatus } from '@/gql/graphql'
import { useFragment } from '@/gql'
import { UsersFragment } from '@/components/Workflows/Pages/Groups/graphql/fragment'

const emit = defineEmits(['submitted', 'submitting', 'error', 'cancel'])

type Group = {
  name: string
  description: string
  status: UserGroupStatus
  id: string
  users: {
    paginatorInfo: {
      total: number
      hasMorePages: boolean
    }
    data: {
      id: string
      name: string
    }[]
  }
}

const props = withDefaults(defineProps<{ group?: Group | null }>(), {
  group: null,
})

const statuses = ref([
  { value: UserGroupStatus.Active, label: 'Active' },
  { value: UserGroupStatus.Inactive, label: 'Inactive' },
  { value: UserGroupStatus.Archived, label: 'Archived' },
])

const inputFocus = ref(false)
const editMode = ref(props.group !== null)
const clientId = 'publicApiCustomFields'

const mapFn = (u) => ({ value: u.id, label: u.name, ...u })

const name = ref(props.group?.name ?? '')
const description = ref(props.group?.description ?? '')
const company = ref(currentAccount.globalId)

const maxNumberOfCharacters = 500
const charactersLeft = computed(() => maxNumberOfCharacters - description.value.length)

const status = (() => {
  const _status = ref()
  return computed({
    get() {
      const groupStatus = props.group?.status ?? UserGroupStatus.Active
      return _status.value ?? { value: groupStatus }
    },
    set(value) {
      _status.value = value
    },
  })
})()

// If the user group has more users we load them all here
const { result: userGroup, loading: loadingAllUsers } = useQuery(
  getAllUsersQuery,
  () => ({
    usersFirst: props.group?.users.paginatorInfo.total ?? 0,
    usersPage: 1,
    id: props.group?.id ?? '',
  }),
  {
    clientId,
    enabled: !!props.group?.users?.paginatorInfo?.hasMorePages,
  },
)

const users = (() => {
  const _users = ref()
  return computed({
    get() {
      return (
        _users.value ??
        useFragment(UsersFragment, userGroup.value?.userGroup)?.users?.data?.map(mapFn) ??
        props.group?.users.data.map(mapFn) ??
        []
      )
    },
    set(val) {
      _users.value = val ?? []
    },
  })
})()

const removeUser = (user) => {
  users.value = users.value.filter((u) => u.id !== user.id)
}

const variables = reactive({
  companyId: company.value,
  first: 50,
  page: 1,
  search: '',
})

// When a user clicks on the input to change their search term it will toggle the dropdown, which feels awkward.
// This prevents that toggle from triggering making the UX feel better and more intuitive
const handleClickOnSelectInput = (event) => {
  if (variables.search !== '') {
    event.stopPropagation()
  }
}

const { result, loading, fetchMore } = useQuery(teamMembersAndOwnersQuery, () => variables, {
  clientId,
})


function getMore() {
  if (!loading.value) {
    // This logic prevents accidentally loading in an old page of data resulting in duplicated data
    const { hasMorePages, currentPage } =
      result.value?.company?.teamMembersAndOwners?.paginatorInfo ?? {}

    if (!hasMorePages || currentPage === undefined) return

    fetchMore({
      variables: {
        page: currentPage + 1,
      },
    })
  }
}



const mapper = computed(() => {
  const mapped = result.value?.company?.teamMembersAndOwners?.data.map(mapFn) ?? []

  // Fix for WdsSelect being confused by updating the list after giving it a v-model start
  const usersMap = mapped.reduce((acc, u) => ({ ...acc, [u.value]: u }), {})
  return users.value.filter((u) => !usersMap[u.value]).concat(mapped)
})


const state = computed(() => ({
  name: name.value,
  description: description.value,
  status: status.value.value,
  users: users.value.map((u) => u.id),
}))

const {
  mutate: createGroup,
  loading: createGroupLoading,
  error: createUserGroupError,
} = useMutation(createUserGroupMutation, () => ({
  clientId,
}))

const {
  mutate: editGroup,
  loading: editGroupLoading,
  error: editUserGroupError,
} = useMutation(editUserGroupMutation, () => ({
  clientId,
}))

const { mutate: deleteGroupMutation, loading: deleteLoading } = useMutation(
  deleteUserGroupMutation,
  () => ({
    clientId,
    update(cache, { data }) {
      if (data?.deleteUserGroup) {
        cache.evict({ id: cache.identify(data.deleteUserGroup) })
        cache.gc()
      }
    },
  }),
)

const submitLoading = computed(
  () => createGroupLoading.value || editGroupLoading.value || deleteLoading.value,
)

const vul = useCustomVuelidate(
  gql`
    input UnifiedUserGroupInput {
      name: String!
      description: String
      status: UserGroupStatus!
      users: [ID!]
    }
  `,
  state,
)

const { error, nameReset, touchAll, quietErrors } = vul

const {
  name: nameError,
  users: usersError,
  description: descriptionError,
  status: statusError,
} = useApolloAndVuelidate(
  useApolloError(editMode.value ? editUserGroupError : createUserGroupError),
  vul,
  'input',
)

const submitForm = async () => {
  touchAll()

  if (error.value) return

  try {
    emit('submitting')
    if (!editMode.value) {
      const input = {
        ...unref(state),
        company: company.value,
      }
      const res = await createGroup({ input, usersFirst: 10, usersPage: 1 })
      emit('submitted', res)
    } else if (props.group !== null) {
      // We get the first 10 users here to conform with the expected number of users in the cache to then update the cache
      // rather than having to make a full refetch
      const input = {
        ...unref(state),
        id: props.group.id,
      }
      const res = await editGroup({ input, usersFirst: 10, usersPage: 1 })
      emit('submitted', res)
    }
  } catch (e) {
    emit('error', e)
  }
}

const showDeleteModal = ref(false)

const deleteGroup = async () => {
  try {
    if (props.group !== null) {
      emit('submitting')
      showDeleteModal.value = false
      await deleteGroupMutation({ input: { id: props.group.id } })
      emit('submitted')
    }
  } catch (e) {
    emit('error', e)
  }
}

const handleInput = (inputValue) => {
  nameReset()
  name.value = inputValue
}

</script>

<style lang="scss" scoped>

svg.inline {
  display: inline;
}

.edit-group-modal {
  &:deep(.wds-input-area-wrapper) {
    .wds-label {
      @apply tw-w-full;
    }
  }
}

.selected-list {
  border: solid 1px;
  @apply tw-rounded;
  @apply tw-border-gray-300;
  .selected-list-item {
    border-bottom: solid 1px;
    @apply tw-border-gray-300;
    @apply tw-p-2;
    &:last-of-type {
      border-bottom: 0;
    }
  }
}
</style>
